import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Briefcase, ChevronLeft, Copy, DollarSign, Gavel, Loader2, Sparkles, Users } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { useEngagementTracker } from "@/hooks/useEngagementTracker";

const CaseIntelligencePage = () => {
  const [analysisType, setAnalysisType] = useState('settlement');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    case_type: 'personal_injury',
    jurisdiction: 'california',
    client_position: 'plaintiff',
    case_summary: '',
    damages_claimed: '',
    strengths: '',
    weaknesses: '',
    judge_name: '',
    attorney_name: '',
    verify_with_multiple_models: true
  });
  const { trackEvent } = useEngagementTracker();
  
  useEffect(() => {
    trackEvent('intelligence', 'view');
  }, [trackEvent]);
  
  const runAnalysis = async () => {
    setLoading(true);
    try {
      let endpoint = '/intelligence/settlement';
      let payload = {};
      
      if (analysisType === 'settlement' || analysisType === 'case') {
        endpoint = `/intelligence/${analysisType}`;
        payload = {
          case_type: form.case_type,
          jurisdiction: form.jurisdiction,
          client_position: form.client_position,
          case_summary: form.case_summary,
          damages_claimed: parseFloat(form.damages_claimed) || 0,
          strengths: form.strengths.split('\n').filter(s => s.trim()),
          weaknesses: form.weaknesses.split('\n').filter(s => s.trim()),
          verify_with_multiple_models: form.verify_with_multiple_models
        };
      } else if (analysisType === 'judge') {
        endpoint = '/intelligence/judge';
        payload = {
          judge_name: form.judge_name,
          jurisdiction: form.jurisdiction,
          case_type: form.case_type,
          verify_with_multiple_models: form.verify_with_multiple_models
        };
      } else if (analysisType === 'attorney') {
        endpoint = '/intelligence/attorney';
        payload = {
          attorney_name: form.attorney_name,
          jurisdiction: form.jurisdiction,
          case_type: form.case_type,
          verify_with_multiple_models: form.verify_with_multiple_models
        };
      }
      
      const res = await axios.post(`${API}${endpoint}`, payload);
      setResult(res.data);
      trackEvent('intelligence', 'analysis_complete', { type: analysisType });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const ANALYSIS_TYPES = [
    { value: 'settlement', label: 'Settlement Intelligence', desc: 'Case valuation & negotiation strategy', icon: DollarSign },
    { value: 'case', label: 'Case Intelligence', desc: 'Comprehensive case assessment', icon: Briefcase },
    { value: 'judge', label: 'Judge Intelligence', desc: 'Judicial pattern analysis', icon: Gavel },
    { value: 'attorney', label: 'Attorney Intelligence', desc: 'Opposing counsel analysis', icon: Users }
  ];
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="case-intelligence-page">
      <Disclaimer />
      <div className="container-shell py-8">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Case Intelligence</h1>
            <Badge className="bg-amber-500">Multi-AI Verified</Badge>
          </div>
          <p className="text-muted-foreground">AI-powered analysis verified across multiple models with confidence scoring</p>
        </div>
        
        <Alert className="mb-6 bg-amber-50 border-amber-200">
          <Sparkles className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            This tool queries 3 AI models (GPT-4o, Claude, Gemini), compares their analyses, and provides 
            a confidence score based on agreement. Higher confidence = greater consensus across models.
          </AlertDescription>
        </Alert>
        
        <div className="grid md:grid-cols-3 gap-6">
          {/* Analysis Type Selection */}
          <div className="space-y-4">
            <Label>Analysis Type</Label>
            {ANALYSIS_TYPES.map(at => (
              <Card
                key={at.value}
                className={`cursor-pointer transition-all ${analysisType === at.value ? 'border-blue-500 bg-blue-50' : 'hover:border-slate-300'}`}
                onClick={() => setAnalysisType(at.value)}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${analysisType === at.value ? 'bg-blue-600 text-white' : 'bg-slate-100'}`}>
                    <at.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-medium">{at.label}</div>
                    <div className="text-xs text-muted-foreground">{at.desc}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Input Form */}
          <Card>
            <CardHeader>
              <CardTitle>Analysis Input</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(analysisType === 'settlement' || analysisType === 'case') && (
                <>
                  <div>
                    <Label>Case Summary</Label>
                    <Textarea
                      value={form.case_summary}
                      onChange={e => setForm(prev => ({ ...prev, case_summary: e.target.value }))}
                      placeholder="Describe the case facts, injuries, and circumstances..."
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Case Type</Label>
                      <Select value={form.case_type} onValueChange={v => setForm(prev => ({ ...prev, case_type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="personal_injury">Personal Injury</SelectItem>
                          <SelectItem value="employment">Employment</SelectItem>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="medical_malpractice">Medical Malpractice</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Jurisdiction</Label>
                      <Select value={form.jurisdiction} onValueChange={v => setForm(prev => ({ ...prev, jurisdiction: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="california">California</SelectItem>
                          <SelectItem value="new_york">New York</SelectItem>
                          <SelectItem value="texas">Texas</SelectItem>
                          <SelectItem value="florida">Florida</SelectItem>
                          <SelectItem value="federal">Federal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Damages Claimed ($)</Label>
                    <Input
                      type="number"
                      value={form.damages_claimed}
                      onChange={e => setForm(prev => ({ ...prev, damages_claimed: e.target.value }))}
                      placeholder="450000"
                    />
                  </div>
                  <div>
                    <Label>Strengths (one per line)</Label>
                    <Textarea
                      value={form.strengths}
                      onChange={e => setForm(prev => ({ ...prev, strengths: e.target.value }))}
                      placeholder="Clear liability&#10;Strong documentation&#10;Sympathetic plaintiff"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Weaknesses (one per line)</Label>
                    <Textarea
                      value={form.weaknesses}
                      onChange={e => setForm(prev => ({ ...prev, weaknesses: e.target.value }))}
                      placeholder="Prior back complaints&#10;Gap in treatment"
                      rows={3}
                    />
                  </div>
                </>
              )}
              
              {analysisType === 'judge' && (
                <>
                  <div>
                    <Label>Judge Name</Label>
                    <Input
                      value={form.judge_name}
                      onChange={e => setForm(prev => ({ ...prev, judge_name: e.target.value }))}
                      placeholder="e.g., Tanya Chutkan"
                    />
                  </div>
                  <div>
                    <Label>Jurisdiction</Label>
                    <Select value={form.jurisdiction} onValueChange={v => setForm(prev => ({ ...prev, jurisdiction: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="california">California</SelectItem>
                        <SelectItem value="dc_federal">DC Federal</SelectItem>
                        <SelectItem value="new_york">New York</SelectItem>
                        <SelectItem value="federal">Federal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {analysisType === 'attorney' && (
                <>
                  <div>
                    <Label>Attorney Name</Label>
                    <Input
                      value={form.attorney_name}
                      onChange={e => setForm(prev => ({ ...prev, attorney_name: e.target.value }))}
                      placeholder="e.g., John Smith"
                    />
                  </div>
                  <div>
                    <Label>Jurisdiction</Label>
                    <Select value={form.jurisdiction} onValueChange={v => setForm(prev => ({ ...prev, jurisdiction: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="california">California</SelectItem>
                        <SelectItem value="new_york">New York</SelectItem>
                        <SelectItem value="texas">Texas</SelectItem>
                        <SelectItem value="florida">Florida</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="multiModel"
                  checked={form.verify_with_multiple_models}
                  onChange={e => setForm(prev => ({ ...prev, verify_with_multiple_models: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="multiModel" className="text-sm cursor-pointer">
                  Enable multi-model verification (3 AI models)
                </Label>
              </div>
              
              <Button onClick={runAnalysis} disabled={loading} className="w-full">
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Analyzing with {form.verify_with_multiple_models ? '3' : '1'} AI models...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Run Analysis
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
          
          {/* Results */}
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Analysis Results</span>
                {result?.verification?.confidence_score && (
                  <Badge className={result.verification.confidence_score >= 70 ? 'bg-green-500' : result.verification.confidence_score >= 50 ? 'bg-amber-500' : 'bg-red-500'}>
                    {result.verification.confidence_score}% Confidence
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {result ? (
                <div className="space-y-4">
                  {result.verification && (
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <div className="flex items-center justify-between text-sm">
                        <span>Agreement Level:</span>
                        <Badge variant="outline">{result.verification.agreement_level}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span>Models Queried:</span>
                        <span>{result.verification.models_queried}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="prose prose-sm max-w-none">
                    <div className="bg-slate-50 p-4 rounded-lg whitespace-pre-wrap text-sm max-h-96 overflow-y-auto">
                      {result.primary_analysis}
                    </div>
                  </div>
                  
                  <Button variant="outline" className="w-full" onClick={() => navigator.clipboard.writeText(result.primary_analysis)}>
                    <Copy className="h-4 w-4 mr-2" /> Copy Analysis
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Configure your analysis and click Run to see results</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </main>
  );
};


export default CaseIntelligencePage;
